import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { formatInr } from "./runPayrollUtils";

function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return formatInr(n);
}

function monthLabel(ym) {
  const s = String(ym || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return s || "—";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[Number(m[2]) - 1] || m[2]} ${m[1]}`;
}

export default function LopReversalBreakdownModal({ darkMode = false, row, onClose }) {
  const detail = row?.lopReversalDetail || null;
  const months = Array.isArray(detail?.months) ? detail.months : [];

  if (!row || typeof document === "undefined") return null;

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";

  return createPortal(
    <div
      className="fixed inset-0 z-[100060] flex items-start justify-center bg-black/50 p-4 pt-16 pb-8"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`flex max-h-[calc(100vh-5rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl border shadow-xl ${
          darkMode
            ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lopr-breakdown-title"
      >
        <div className={`flex shrink-0 items-center justify-between border-b px-4 py-3 ${border}`}>
          <div>
            <h3 id="lopr-breakdown-title" className="text-sm font-semibold">
              {row.empCode || "—"} — {row.name || "Employee"} · LOP reversal amount
            </h3>
            <p className={`mt-0.5 text-[11px] ${muted}`}>
              {detail?.formula ||
                "Pay-days monthly gross ÷ calendar days of the original LOP month × reversed days"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded p-1 ${muted} hover:bg-[hsl(var(--ds-muted))]`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-3 space-y-3">
          <div className={`rounded-lg border px-3 py-2 text-xs ${border}`}>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <span>
                Payroll month: <strong>{monthLabel(row.payMonth || detail?.payMonth)}</strong>
              </span>
              <span>
                Reversed days: <strong>{row.lopReversalDays ?? detail?.lopReversalDays ?? 0}</strong>
              </span>
              <span>
                Amount: <strong>{money(row.lopReversalAmount ?? detail?.lopReversalAmount)}</strong>
              </span>
            </div>
          </div>

          {months.length === 0 ? (
            <p className={`text-sm ${muted}`}>No calculation detail available. Re-validate this step.</p>
          ) : (
            months.map((m) => (
              <div key={m.lopMonth} className={`rounded-lg border ${border}`}>
                <div className="border-b px-3 py-2 text-xs">
                  <p className="font-semibold">
                    Original LOP month {monthLabel(m.lopMonth)} · {m.reversedDays} day(s)
                  </p>
                  <p className={`mt-0.5 ${muted}`}>
                    Dates: {(m.dates || []).join(", ") || "—"}
                  </p>
                  <p className={`mt-1 tabular-nums ${muted}`}>
                    ₹{Number(m.monthlyGross || 0).toFixed(2)} ÷ {m.calendarDays} days ×{" "}
                    {m.reversedDays} = <strong className="text-[hsl(var(--ds-fg))]">{money(m.amount)}</strong>
                    {m.source === "ctc_fallback" ? " (CTC/12 fallback)" : ""}
                  </p>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--ds-muted))]">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-semibold">Pay-days component</th>
                      <th className="px-3 py-1.5 text-right font-semibold">Monthly</th>
                      <th className="px-3 py-1.5 text-right font-semibold">Daily</th>
                      <th className="px-3 py-1.5 text-right font-semibold">Reversal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(m.components || []).map((c) => (
                      <tr key={c.name} className={`border-t ${border}`}>
                        <td className="px-3 py-1.5">{c.name}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{money(c.monthly)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{money(c.dailyRate)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-medium">{money(c.amount)}</td>
                      </tr>
                    ))}
                    {!(m.components || []).length ? (
                      <tr>
                        <td colSpan={4} className={`px-3 py-3 text-center ${muted}`}>
                          No pay-days earning components on the structure for this month.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                  {(m.components || []).length ? (
                    <tfoot>
                      {(() => {
                        const gross = (m.components || []).reduce(
                          (s, c) => s + (Number(c.amount) || 0),
                          0
                        );
                        const deductions = 0;
                        const net = gross - deductions;
                        return (
                          <>
                            <tr className={`border-t ${border} bg-[hsl(var(--ds-muted))]`}>
                              <td className="px-3 py-1.5 font-semibold" colSpan={3}>
                                Gross total difference
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                                {money(gross)}
                              </td>
                            </tr>
                            <tr className={`border-t ${border}`}>
                              <td className="px-3 py-1.5 font-semibold" colSpan={3}>
                                Deductions total difference
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                                {money(deductions)}
                              </td>
                            </tr>
                            <tr className={`border-t ${border} bg-[hsl(var(--ds-muted))]`}>
                              <td className="px-3 py-1.5 font-semibold" colSpan={3}>
                                Net pay difference
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                                {money(net)}
                              </td>
                            </tr>
                          </>
                        );
                      })()}
                    </tfoot>
                  ) : null}
                </table>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
